/**
 * Firebase Authentication Login Form
 * 
 * Provides Google OAuth and email/password authentication options
 */

import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface LoginFormProps {
  onLoginSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const { signInWithGoogle, signInWithEmail, createAccount, loading, error } = useAuth();
  
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: ''
  });
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
    displayName: ''
  });

  const handleInputChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors = {
      email: '',
      password: '',
      displayName: ''
    };

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (isSignUp && !formData.displayName) {
      errors.displayName = 'Display name is required';
    }

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle('offshore-sails');
      onLoginSuccess?.();
    } catch (err) {
      console.error('Google sign-in failed:', err);
    }
  };

  const handleEmailAuth = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      if (isSignUp) {
        await createAccount(formData.email, formData.password, formData.displayName);
      } else {
        await signInWithEmail(formData.email, formData.password, 'offshore-sails');
      }
      onLoginSuccess?.();
    } catch (err) {
      console.error('Email authentication failed:', err);
    }
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Welcome to Offshore Sails
      </Typography>
      
      <Typography variant="h6" component="h2" gutterBottom align="center">
        {isSignUp ? 'Create Account' : 'Sign In'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Google Sign In Only */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignIn}
        disabled={loading}
        sx={{ mb: 2 }}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          `Sign in with Google`
        )}
      </Button>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        Use your Google account to access the application
      </Typography>
    </Paper>
  );
};